// src/services/trophyStorage.js

const LS_KEY = "pwp_trophies_v1";

/**
 * ✅ IMPORTANT (V1.1)
 * This file is the "single source of truth" for:
 * - Trophy Cabinet history (rounds[])
 * - The current run in progress (roundInProgress)
 *
 * Your LevelTemplate currently ALSO stores the StarQuest meter in a separate key:
 *   const STARQUEST_STORAGE_KEY = "tt_starquest_completed_levels_v1";
 *
 * So when a trophy is collected and the game restarts at Level-1,
 * we must ALSO reset that StarQuest key so stars can be collected again.
 */

/**
 * ✅ Default requirements:
 * - A "Completion" = full Level 1 → 10 journey
 * - Stars are collected on L2–L9 = 8 stars total
 */
const DEFAULT_REQUIREMENTS = {
  requiredLevels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  requiredStarsCount: 8,
};

/**
 * ✅ KEYS TO RESET ON "NEW RUN" (after Collect Statue)
 * These are "progress for the current run" keys that must reset so the kid can replay.
 *
 * ✅ IMPORTANT:
 * Keep this list STRICTLY run-only. Do NOT put paywall / entitlement keys here.
 * This ensures passes (daily/weekly/monthly) remain time-based and unaffected by replay runs.
 */
const RUN_PROGRESS_KEYS_TO_RESET = [
  // ✅ StarQuest bar (stars collected meter) — run-only
  "tt_starquest_completed_levels_v1",
];

function safeParse(json, fallback) {
  try {
    const v = JSON.parse(json);
    return v ?? fallback;
  } catch {
    return fallback;
  }
}

function nowMs() {
  return Date.now();
}

function hasLocalStorage() {
  return typeof window !== "undefined" && !!window.localStorage;
}

// Local YYYY-MM-DD (important for streaks)
function ymdLocal(ts = nowMs()) {
  const d = new Date(ts);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Local HH:MM (for display)
function hmLocal(ts = nowMs()) {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

// Local YYYY-MM-DD • HH:MM
function ymdHmLocal(ts = nowMs()) {
  return `${ymdLocal(ts)} • ${hmLocal(ts)}`;
}

function defaultState() {
  return {
    playerName: "",
    roundInProgress: null,
    // each: { id, roundNumber, startedAt, completedAt, durationMs, tier, meta? }
    rounds: [],
    personalBestMs: null,
    currentStreakDays: 0,
    longestStreakDays: 0,
    lastCompletionYmd: null,
    updatedAt: nowMs(),
  };
}

/** Convert older roundInProgress shapes into the new shape (back-compat). */
function normalizeRoundInProgress(rip) {
  if (!rip) return null;

  // Old shape: { startedAt, startedYmd }
  const startedAt = typeof rip.startedAt === "number" ? rip.startedAt : nowMs();

  const startedYmd =
    typeof rip.startedYmd === "string" ? rip.startedYmd : ymdLocal(startedAt);

  const levelsCompleted =
    rip.levelsCompleted && typeof rip.levelsCompleted === "object"
      ? rip.levelsCompleted
      : {}; // { "1": true, ... }

  const starsCollected =
    rip.starsCollected && typeof rip.starsCollected === "object"
      ? rip.starsCollected
      : {}; // { "L2": true, ... } or { "zebra": true, ... }

  const lastLevelCompleted =
    typeof rip.lastLevelCompleted === "number" ? rip.lastLevelCompleted : null;

  return {
    startedAt,
    startedYmd,
    levelsCompleted,
    starsCollected,
    lastLevelCompleted,
    updatedAt: typeof rip.updatedAt === "number" ? rip.updatedAt : nowMs(),
  };
}

function readState() {
  // ✅ Guard for non-browser environments (SSR/build tools)
  if (!hasLocalStorage()) return defaultState();

  const raw = window.localStorage.getItem(LS_KEY);
  const state = raw ? safeParse(raw, defaultState()) : defaultState();

  // safety: ensure required keys
  if (!state.rounds) state.rounds = [];
  if (!("playerName" in state)) state.playerName = "";
  if (!("roundInProgress" in state)) state.roundInProgress = null;

  // ✅ Back-compat: upgrade stored roundInProgress if needed
  state.roundInProgress = normalizeRoundInProgress(state.roundInProgress);

  return state;
}

function writeState(next) {
  const state = { ...next, updatedAt: nowMs() };

  // ✅ Guard for non-browser environments (SSR/build tools)
  if (!hasLocalStorage()) return state;

  window.localStorage.setItem(LS_KEY, JSON.stringify(state));
  return state;
}

/**
 * ✅ MILESTONES:
 * - every 5 statues  -> Bronze
 * - every 50 statues -> Silver
 * - every 100 statues -> Gold
 */
function computeTier(roundNumber) {
  const n = Number(roundNumber);
  if (!Number.isFinite(n) || n <= 0) return "normal";
  if (n % 100 === 0) return "gold";
  if (n % 50 === 0) return "silver";
  if (n % 5 === 0) return "bronze";
  return "normal";
}

function computeNextStreak({
  lastCompletionYmd,
  currentStreakDays,
  longestStreakDays,
}) {
  const today = ymdLocal();

  if (!lastCompletionYmd) {
    const s = 1;
    return {
      currentStreakDays: s,
      longestStreakDays: Math.max(longestStreakDays || 0, s),
      lastCompletionYmd: today,
    };
  }

  // If already completed today, streak unchanged (but we still record a round)
  if (lastCompletionYmd === today) {
    const cur = currentStreakDays || 1;
    return {
      currentStreakDays: cur,
      longestStreakDays: Math.max(longestStreakDays || 0, cur),
      lastCompletionYmd,
    };
  }

  // compare with yesterday (local time)
  const t = new Date();
  const yesterday = new Date(t.getFullYear(), t.getMonth(), t.getDate() - 1);
  const ymdYesterday = ymdLocal(yesterday.getTime());

  if (lastCompletionYmd === ymdYesterday) {
    const s = (currentStreakDays || 0) + 1;
    return {
      currentStreakDays: s,
      longestStreakDays: Math.max(longestStreakDays || 0, s),
      lastCompletionYmd: today,
    };
  }

  // break in streak
  const s = 1;
  return {
    currentStreakDays: s,
    longestStreakDays: Math.max(longestStreakDays || 0, s),
    lastCompletionYmd: today,
  };
}

function makeId() {
  return `r_${nowMs()}_${Math.random().toString(16).slice(2)}`;
}

function countTruthyKeys(obj) {
  if (!obj || typeof obj !== "object") return 0;
  return Object.values(obj).filter(Boolean).length;
}

function normalizeLevelKey(levelId) {
  // always store as string keys: "1", "2", ...
  const n = Number(levelId);
  if (!Number.isFinite(n) || n <= 0) return null;
  return String(n);
}

/**
 * Star key strategy:
 * - Keep it flexible: you can pass "L2" or "zebra" etc.
 * - For your current plan: easiest is markStarCollected(`L${levelId}`) for L2–L9.
 */
function normalizeStarKey(starKey) {
  const s = String(starKey || "").trim();
  return s ? s : null;
}

function ensureRoundInProgressExists(s) {
  // If caller forgets startRound(), we still create a roundInProgress
  if (s.roundInProgress?.startedAt) return s;

  const startedAt = nowMs();
  return {
    ...s,
    roundInProgress: {
      startedAt,
      startedYmd: ymdLocal(startedAt),
      levelsCompleted: {},
      starsCollected: {},
      lastLevelCompleted: null,
      updatedAt: nowMs(),
    },
  };
}

function getEligibility(s, req = DEFAULT_REQUIREMENTS) {
  const rip = normalizeRoundInProgress(s.roundInProgress);
  if (!rip) {
    return {
      ok: false,
      reason: "no_round_in_progress",
      levelsDone: 0,
      levelsNeeded: req.requiredLevels?.length || 0,
      starsDone: 0,
      starsNeeded: req.requiredStarsCount || 0,
      missingLevels: req.requiredLevels || [],
    };
  }

  const levelsNeededArr = Array.isArray(req.requiredLevels)
    ? req.requiredLevels
    : [];

  const levelsNeededKeys = levelsNeededArr
    .map(normalizeLevelKey)
    .filter(Boolean);

  const levelsCompleted = rip.levelsCompleted || {};
  const starsCollected = rip.starsCollected || {};

  const levelsDone = levelsNeededKeys.filter((k) => !!levelsCompleted[k]).length;
  const levelsNeeded = levelsNeededKeys.length;

  const starsDone = countTruthyKeys(starsCollected);
  const starsNeeded =
    typeof req.requiredStarsCount === "number" ? req.requiredStarsCount : 0;

  const missingLevels = levelsNeededKeys
    .filter((k) => !levelsCompleted[k])
    .map((k) => Number(k));

  const ok = levelsDone >= levelsNeeded && starsDone >= starsNeeded;

  return {
    ok,
    reason: ok ? null : "requirements_not_met",
    levelsDone,
    levelsNeeded,
    starsDone,
    starsNeeded,
    missingLevels,
  };
}

/**
 * ✅ Reset "run progress" keys (StarQuest meter, etc.)
 * We DO NOT touch trophies (LS_KEY) or any paywall/entitlement keys.
 */
function resetRunProgressKeys(keys = RUN_PROGRESS_KEYS_TO_RESET) {
  if (!hasLocalStorage()) return;
  const list = Array.isArray(keys) ? keys : [];
  list.forEach((k) => {
    const key = String(k || "").trim();
    if (!key) return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  });
}

export const TrophyStorage = {
  // Read-only
  getState() {
    return readState();
  },

  // Player name
  setPlayerName(name) {
    const s = readState();
    return writeState({ ...s, playerName: String(name || "").trim() });
  },

  /**
   * Round lifecycle
   * startRound() should run ONCE when Level-1 gameplay begins.
   */
  startRound() {
    let s = readState();

    // If a round is already in progress, don’t overwrite it.
    if (s.roundInProgress?.startedAt) return s;

    const startedAt = nowMs();
    const next = {
      ...s,
      roundInProgress: {
        startedAt,
        startedYmd: ymdLocal(startedAt),
        levelsCompleted: {},
        starsCollected: {},
        lastLevelCompleted: null,
        updatedAt: nowMs(),
      },
    };

    return writeState(next);
  },

  cancelRound() {
    const s = readState();
    if (!s.roundInProgress) return s;
    return writeState({ ...s, roundInProgress: null });
  },

  /**
   * Mark that a level was completed during the current run.
   * Call this when a level success condition happens.
   * Example: TrophyStorage.markLevelComplete(1)
   */
  markLevelComplete(levelId) {
    let s = readState();
    s = ensureRoundInProgressExists(s);

    const key = normalizeLevelKey(levelId);
    if (!key) return s;

    const rip = normalizeRoundInProgress(s.roundInProgress);

    const nextRip = {
      ...rip,
      levelsCompleted: { ...(rip.levelsCompleted || {}), [key]: true },
      lastLevelCompleted: Number(levelId),
      updatedAt: nowMs(),
    };

    return writeState({ ...s, roundInProgress: nextRip });
  },

  /**
   * Mark that a star was collected during the current run.
   * For your current plan (L2–L9 are the 8 stars), simplest:
   *   TrophyStorage.markStarCollected(`L${levelId}`)
   */
  markStarCollected(starKey) {
    let s = readState();
    s = ensureRoundInProgressExists(s);

    const k = normalizeStarKey(starKey);
    if (!k) return s;

    const rip = normalizeRoundInProgress(s.roundInProgress);

    const nextRip = {
      ...rip,
      starsCollected: { ...(rip.starsCollected || {}), [k]: true },
      updatedAt: nowMs(),
    };

    return writeState({ ...s, roundInProgress: nextRip });
  },

  /**
   * Convenience: complete a level + star in one go (useful for L2–L9).
   * Example: TrophyStorage.markLevelAndStar(2)  // marks level 2 and star "L2"
   */
  markLevelAndStar(levelId, starKey = null) {
    let s = readState();
    s = ensureRoundInProgressExists(s);

    const key = normalizeLevelKey(levelId);
    if (!key) return s;

    const rip = normalizeRoundInProgress(s.roundInProgress);
    const starsCollected = { ...(rip.starsCollected || {}) };

    const sk = normalizeStarKey(starKey || `L${Number(levelId)}`);
    if (sk) starsCollected[sk] = true;

    const nextRip = {
      ...rip,
      levelsCompleted: { ...(rip.levelsCompleted || {}), [key]: true },
      starsCollected,
      lastLevelCompleted: Number(levelId),
      updatedAt: nowMs(),
    };

    return writeState({ ...s, roundInProgress: nextRip });
  },

  /**
   * Used by the Level-10 Victory modal to enable/disable Collect Statue.
   */
  canCollectStatue(requirements = DEFAULT_REQUIREMENTS) {
    const s = readState();
    return getEligibility(s, requirements);
  },

  /**
   * Optional manual reset:
   * Clears run-only progress keys (StarQuest meter) without deleting trophies.
   */
  resetForNewRun(options = {}) {
    const { keys = RUN_PROGRESS_KEYS_TO_RESET } = options;
    resetRunProgressKeys(keys);

    // also clear any active run (safety)
    const s = readState();
    if (s.roundInProgress) writeState({ ...s, roundInProgress: null });
    return true;
  },

  /**
   * Finalize the run ONLY when user clicks "Collect Statue".
   * By default, we enforce requirements (full 1→10 + 8 stars).
   *
   * ✅ IMPORTANT:
   * After successfully saving the trophy, we also reset the run-only StarQuest key(s)
   * so the full star collection experience is fresh again for the next run.
   *
   * ✅ Also IMPORTANT:
   * This reset does NOT affect passes/entitlements because we do not touch any paywall keys.
   */
  completeRound(options = {}) {
    const {
      enforceRequirements = true,
      requirements = DEFAULT_REQUIREMENTS,
      meta = {}, // anything you want to store with the round (optional)
      // ✅ allow override of reset keys if you ever need
      resetRunKeys = RUN_PROGRESS_KEYS_TO_RESET,
      // ✅ set false if you DON'T want auto-reset (rare)
      autoResetRunProgress = true,
    } = options;

    const s0 = readState();
    const eligibility = getEligibility(s0, requirements);

    if (enforceRequirements && !eligibility.ok) {
      return {
        saved: s0,
        round: null,
        error: "cannot_collect_statue_yet",
        eligibility,
      };
    }

    const startedAt = s0.roundInProgress?.startedAt;

    // If for any reason startRound wasn’t called, fallback to "now" (duration = 0)
    const safeStartedAt = typeof startedAt === "number" ? startedAt : nowMs();

    const completedAt = nowMs();
    const durationMs = Math.max(0, completedAt - safeStartedAt);

    const nextRoundNumber = (s0.rounds?.length || 0) + 1;
    const tier = computeTier(nextRoundNumber);

    const rip = normalizeRoundInProgress(s0.roundInProgress);

    const round = {
      id: makeId(),
      roundNumber: nextRoundNumber,
      startedAt: safeStartedAt,
      completedAt,
      durationMs,
      tier,
      meta: {
        startedYmd: rip?.startedYmd || ymdLocal(safeStartedAt),

        // completion date + time (local)
        completedYmd: ymdLocal(completedAt),
        completedHm: hmLocal(completedAt),
        completedYmdHm: ymdHmLocal(completedAt),

        levelsCompleted: rip?.levelsCompleted || {},
        starsCollected: rip?.starsCollected || {},
        ...meta,
      },
    };

    // personal best
    const personalBestMs =
      typeof s0.personalBestMs === "number"
        ? Math.min(s0.personalBestMs, durationMs)
        : durationMs;

    // streaks
    const streakUpdate = computeNextStreak({
      lastCompletionYmd: s0.lastCompletionYmd,
      currentStreakDays: s0.currentStreakDays,
      longestStreakDays: s0.longestStreakDays,
    });

    const next = {
      ...s0,
      rounds: [round, ...(s0.rounds || [])], // newest first
      personalBestMs,
      ...streakUpdate,
      roundInProgress: null, // end run
    };

    const saved = writeState(next);

    // ✅ AUTO RESET: wipe run-only progress so user can collect stars again
    if (autoResetRunProgress) {
      try {
        resetRunProgressKeys(resetRunKeys);
      } catch {
        // ignore
      }
    }

    return { saved, round, eligibility };
  },

  resetAll() {
    // This deletes trophy cabinet history too (use carefully!)
    if (hasLocalStorage()) window.localStorage.removeItem(LS_KEY);
    return defaultState();
  },
};
